// src/services/orderDeskAdminAPI.js
// Admin order operations using direct OrderDesk API
// SEPARATE from cart submission flow - does not affect existing orderDeskAPI.js

import OrderDeskDirectClient from './orderDeskDirectClient.js';

class OrderDeskAdminAPI {
    constructor() {
        this.client = new OrderDeskDirectClient();
    }

    // ═══════════════════════════════════════════════════════════════════════════════
    // ORDER OPERATIONS
    // ═══════════════════════════════════════════════════════════════════════════════

    /**
     * Get a single order by OrderDesk order ID
     */
    async getOrder(orderId) {
        try {
            console.log(`[OrderDeskAdminAPI] Getting order: ${orderId}`);
            const result = await this.client.makeRequest(`/orders/${orderId}`);
            
            return {
                success: true,
                order: result.data?.order || result.data,
                meta: result.meta
            };
        } catch (error) {
            console.error(`[OrderDeskAdminAPI] Failed to get order ${orderId}:`, error);
            return {
                success: false,
                error: error.message,
                status: error.status
            };
        }
    }

    /**
     * Search for orders by source_id, email, etc.
     */
    async searchOrders(searchParams = {}) {
        try {
            console.log(`[OrderDeskAdminAPI] Searching orders:`, searchParams);
            
            const params = {
                limit: searchParams.limit || 50,
                order_by: searchParams.orderBy || 'date_updated',
                order: searchParams.order || 'DESC',
                ...searchParams
            };
            
            const result = await this.client.makeRequest('/orders', { params });
            
            return {
                success: true,
                orders: result.data?.orders || [],
                count: result.data?.count || 0,
                meta: result.meta
            };
        } catch (error) {
            console.error(`[OrderDeskAdminAPI] Failed to search orders:`, error);
            return {
                success: false,
                error: error.message,
                status: error.status
            };
        }
    }

    /**
     * Find order by source_id (for ID resolution)
     */
    async findOrderBySourceId(sourceId) {
        try {
            if (process.env.NODE_ENV === 'development') {
                console.log(`[OrderDeskAdminAPI] Finding order by source_id: ${sourceId}`);
            }
            
            const result = await this.searchOrders({
                source_id: sourceId,
                limit: 5
            });
            
            if (result.success && result.orders.length > 0) {
                return {
                    success: true,
                    found: true,
                    order: result.orders[0],
                    orderId: result.orders[0].id,
                    duplicates: result.orders.length > 1 ? result.orders.slice(1) : [],
                    meta: result.meta
                };
            }
            
            return {
                success: true,
                found: false,
                order: null,
                orderId: null,
                meta: result.meta
            };
        } catch (error) {
            console.error(`[OrderDeskAdminAPI] Failed to find order by source_id ${sourceId}:`, error);
            return {
                success: false,
                error: error.message,
                status: error.status
            };
        }
    }

    /**
     * Create new order (ADMIN ONLY - not for cart submissions)
     */
    async createOrder(orderData) {
        try {
            console.log(`[OrderDeskAdminAPI] Creating order:`, orderData.source_id);
            
            // Validate and normalize
            this.client.validateOrderData(orderData, false);
            const normalized = this.client.normalizeOrderData(orderData);
            
            const result = await this.client.makeRequest('/orders', {
                method: 'POST',
                body: normalized
            });
            
            return {
                success: true,
                order: result.data?.order || result.data,
                orderId: result.data?.order?.id || result.data?.id,
                meta: result.meta
            };
        } catch (error) {
            console.error(`[OrderDeskAdminAPI] Failed to create order:`, error);
            return {
                success: false,
                error: error.message,
                status: error.status,
                validationErrors: error.message.includes('validation') ? error.message : null
            };
        }
    }

    /**
     * Update order by OrderDesk order ID (FIXED VERSION)
     */
    async updateOrder(orderId, orderData) {
        try {
            console.log(`[OrderDeskAdminAPI] Updating order: ${orderId}`);
            
            // Validate and normalize (partial update allowed)
            const normalized = this.client.normalizeOrderData(orderData);
            
            const result = await this.client.makeRequest(`/orders/${orderId}`, {
                method: 'PUT',
                body: normalized
            });
            
            return {
                success: true,
                order: result.data?.order || result.data,
                meta: result.meta
            };
        } catch (error) {
            console.error(`[OrderDeskAdminAPI] Failed to update order ${orderId}:`, error);
            return {
                success: false,
                error: error.message,
                status: error.status
            };
        }
    }

    /**
     * Smart update - resolves OrderDesk ID first, then updates
     */
    async smartUpdateOrder(sourceIdOrOrderId, orderData) {
        try {
            console.log(`[OrderDeskAdminAPI] Smart update for: ${sourceIdOrOrderId}`);
            
            let orderId = sourceIdOrOrderId;
            
            // If it's not a numeric ID, try to resolve by source_id
            if (!/^\d+$/.test(String(sourceIdOrOrderId))) {
                console.log(`[OrderDeskAdminAPI] Resolving source_id: ${sourceIdOrOrderId}`);
                const lookup = await this.findOrderBySourceId(sourceIdOrOrderId);
                
                if (!lookup.success) {
                    return lookup;
                }
                
                if (!lookup.found) {
                    return {
                        success: false,
                        error: `Order not found with source_id: ${sourceIdOrOrderId}`,
                        status: 404
                    };
                }
                
                orderId = lookup.orderId;
                console.log(`[OrderDeskAdminAPI] Resolved to OrderDesk ID: ${orderId}`);
            }
            
            // Now update using the OrderDesk ID
            return await this.updateOrder(orderId, orderData);
        } catch (error) {
            console.error(`[OrderDeskAdminAPI] Smart update failed:`, error);
            return {
                success: false,
                error: error.message,
                status: error.status
            };
        }
    }

    /**
     * Update only order status
     */
    async updateOrderStatus(orderId, status) {
        return await this.updateOrder(orderId, { status });
    }

    /**
     * Update only shipping information
     */
    async updateOrderShipping(orderId, shippingData) {
        const updateData = {};
        
        if (shippingData.shipping_method) {
            updateData.shipping_method = shippingData.shipping_method;
        }
        
        if (shippingData.shipping_total !== undefined) {
            updateData.shipping_total = shippingData.shipping_total;
        }
        
        if (shippingData.shipping) {
            updateData.shipping = shippingData.shipping;
        }
        
        return await this.updateOrder(orderId, updateData);
    }

    /**
     * Delete order
     */
    async deleteOrder(orderId) {
        try {
            console.log(`[OrderDeskAdminAPI] Deleting order: ${orderId}`);
            
            const result = await this.client.makeRequest(`/orders/${orderId}`, {
                method: 'DELETE'
            });
            
            return {
                success: true,
                message: `Order ${orderId} deleted successfully`,
                meta: result.meta
            };
        } catch (error) {
            console.error(`[OrderDeskAdminAPI] Failed to delete order ${orderId}:`, error);
            return {
                success: false,
                error: error.message,
                status: error.status
            };
        }
    }

    /**
     * Smart delete - resolves OrderDesk ID first, then deletes
     */
    async smartDeleteOrder(sourceIdOrOrderId) {
        try {
            console.log(`[OrderDeskAdminAPI] Smart delete for: ${sourceIdOrOrderId}`);
            
            let orderId = sourceIdOrOrderId;
            
            // If it's not a numeric ID, try to resolve by source_id
            if (!/^\d+$/.test(String(sourceIdOrOrderId))) {
                console.log(`[OrderDeskAdminAPI] Resolving source_id: ${sourceIdOrOrderId}`);
                const lookup = await this.findOrderBySourceId(sourceIdOrOrderId);
                
                if (!lookup.success) {
                    return lookup;
                }
                
                if (!lookup.found) {
                    return {
                        success: false,
                        error: `Order not found with source_id: ${sourceIdOrOrderId}`,
                        status: 404
                    };
                }
                
                orderId = lookup.orderId;
                console.log(`[OrderDeskAdminAPI] Resolved to OrderDesk ID: ${orderId}`);
            }
            
            // Now delete using the OrderDesk ID
            return await this.deleteOrder(orderId);
        } catch (error) {
            console.error(`[OrderDeskAdminAPI] Smart delete failed:`, error);
            return {
                success: false,
                error: error.message,
                status: error.status
            };
        }
    }

    // ═══════════════════════════════════════════════════════════════════════════════
    // ORDER ITEMS OPERATIONS
    // ═══════════════════════════════════════════════════════════════════════════════

    /**
     * Add items to existing order
     */
    async addOrderItems(orderId, items) {
        try {
            console.log(`[OrderDeskAdminAPI] Adding items to order: ${orderId}`);
            
            // Validate items
            if (!Array.isArray(items) || items.length === 0) {
                throw new Error('Items must be a non-empty array');
            }
            
            items.forEach((item, index) => {
                if (!item.name) throw new Error(`Item ${index}: name is required`);
                if (!item.code) throw new Error(`Item ${index}: code is required`);
                if (!item.quantity || item.quantity <= 0) throw new Error(`Item ${index}: quantity must be positive`);
                if (!item.price) throw new Error(`Item ${index}: price is required`);
            });
            
            const result = await this.client.makeRequest(`/orders/${orderId}/items`, {
                method: 'POST',
                body: { items }
            });
            
            return {
                success: true,
                items: result.data?.items || result.data,
                meta: result.meta
            };
        } catch (error) {
            console.error(`[OrderDeskAdminAPI] Failed to add items to order ${orderId}:`, error);
            return {
                success: false,
                error: error.message,
                status: error.status
            };
        }
    }

    /**
     * Update specific order item
     */
    async updateOrderItem(orderId, itemId, itemData) {
        try {
            console.log(`[OrderDeskAdminAPI] Updating item ${itemId} in order ${orderId}`);
            
            const result = await this.client.makeRequest(`/orders/${orderId}/items/${itemId}`, {
                method: 'PUT',
                body: itemData
            });
            
            return {
                success: true,
                item: result.data?.item || result.data,
                meta: result.meta
            };
        } catch (error) {
            console.error(`[OrderDeskAdminAPI] Failed to update item ${itemId} in order ${orderId}:`, error);
            return {
                success: false,
                error: error.message,
                status: error.status
            };
        }
    }

    /**
     * Remove item from order
     */
    async removeOrderItem(orderId, itemId) {
        try {
            console.log(`[OrderDeskAdminAPI] Removing item ${itemId} from order ${orderId}`);
            
            const result = await this.client.makeRequest(`/orders/${orderId}/items/${itemId}`, {
                method: 'DELETE'
            });
            
            return {
                success: true,
                message: `Item ${itemId} removed from order ${orderId}`,
                meta: result.meta
            };
        } catch (error) {
            console.error(`[OrderDeskAdminAPI] Failed to remove item ${itemId} from order ${orderId}:`, error);
            return {
                success: false,
                error: error.message,
                status: error.status
            };
        }
    }

    // ═══════════════════════════════════════════════════════════════════════════════
    // BULK OPERATIONS
    // ═══════════════════════════════════════════════════════════════════════════════

    /**
     * Bulk create orders (ADMIN ONLY)
     */
    async bulkCreateOrders(orders) {
        try {
            console.log(`[OrderDeskAdminAPI] Bulk creating ${orders.length} orders`);
            
            // Validate all orders first
            orders.forEach((order, index) => {
                try {
                    this.client.validateOrderData(order, false);
                } catch (error) {
                    throw new Error(`Order ${index}: ${error.message}`);
                }
            });
            
            // Normalize all orders
            const normalized = orders.map(order => this.client.normalizeOrderData(order));
            
            const result = await this.client.makeRequest('/orders/bulk', {
                method: 'POST',
                body: { orders: normalized }
            });
            
            return {
                success: true,
                orders: result.data?.orders || [],
                created: result.data?.created || 0,
                failed: result.data?.failed || 0,
                meta: result.meta
            };
        } catch (error) {
            console.error(`[OrderDeskAdminAPI] Bulk create failed:`, error);
            return {
                success: false,
                error: error.message,
                status: error.status
            };
        }
    }

    // ═══════════════════════════════════════════════════════════════════════════════
    // UTILITY METHODS
    // ═══════════════════════════════════════════════════════════════════════════════

    /**
     * Health check
     */
    async healthCheck() {
        return await this.client.healthCheck();
    }

    /**
     * Get API usage stats
     */
    getUsageStats() {
        return {
            rateLimiter: {
                maxRequests: this.client.rateLimiter.maxRequests,
                windowMs: this.client.rateLimiter.windowMs,
                currentRequests: this.client.rateLimiter.requests.length,
                nextSlotIn: this.client.rateLimiter.requests.length >= this.client.rateLimiter.maxRequests 
                    ? Math.max(0, this.client.rateLimiter.windowMs - (Date.now() - Math.min(...this.client.rateLimiter.requests)))
                    : 0
            }
        };
    }
}

// ═══════════════════════════════════════════════════════════════════════════════
// ORDERDESKER ADMIN FUNCTIONS (Two-way operations via Cloud Run)
// ═══════════════════════════════════════════════════════════════════════════════

const ORDERDESKER_BASE =
    process.env.REACT_APP_ORDERDESKER_BASE ||
    'https://orderdesker-844411985132.europe-west2.run.app';

/**
 * Generic fetch wrapper for Orderdesker admin operations
 */
export async function odFetch(path, { method = 'GET', body } = {}) {
    const url = `${ORDERDESKER_BASE}${path}`;
    const opts = {
        method,
        mode: 'cors',
        headers: { 'Content-Type': 'application/json' },
    };
    if (body) opts.body = JSON.stringify(body);
    
    const res = await fetch(url, opts);
    let data = {};
    try { data = await res.json(); } catch { /* ignore */ }
    
    if (!res.ok) {
        const msg = (data && (data.error || data.message)) || `OrderDesker ${res.status}`;
        throw new Error(msg);
    }
    
    return data;
}

/**
 * Check/get order by source_id
 */
export async function odCheckBySource(source_id) {
    return odFetch(`/?source_id=${encodeURIComponent(source_id)}`, { method: 'GET' });
}

/**
 * Update order by OrderDesk order_id
 * Uses POST for upsert behavior - creates if doesn't exist, updates if exists
 */
export async function odUpdateByOrderId(order_id, fullOrder) {
    return odFetch(`/`, {
        method: 'POST',
        body: { ...fullOrder, source_id: order_id }
    });
}

/**
 * Update order by source_id
 * Uses POST for upsert behavior - creates if doesn't exist, updates if exists
 */
export async function odUpdateBySource(source_id, fullOrder) {
    return odFetch(`/`, {
        method: 'POST',
        body: { ...fullOrder, source_id: source_id }
    });
}

/**
 * Renumber order - updates existing order with new ID/data
 * Uses the new /renumber endpoint for proper order renumbering
 */
export async function odRenumberOrder(oldSourceId, newOrderData) {
    return odFetch(`/renumber`, {
        method: 'POST',
        body: { old_source_id: oldSourceId, new_order_data: newOrderData }
    });
}

/**
 * Delete order by OrderDesk order_id
 */
export async function odDeleteByOrderId(order_id) {
    return odFetch(`/?order_id=${encodeURIComponent(order_id)}`, { method: 'DELETE' });
}

// Export singleton instance
const orderDeskAdminAPI = new OrderDeskAdminAPI();
export default orderDeskAdminAPI;
